<template>
    <div class="slider-wrapper" :class="{ 'modal-mode': modal, 'shown': opened }">
        <div class="slider" ref="slider">
            <div class="item" v-for="(item, index) in items" :key="index" :class="{
                'item-enter': nextIndex === index && isAnimating,
                'item-leave': currentIndex === index && isAnimating,
                'item-current': currentIndex === index
            }">
                <div class="background">
                    <div v-img-responsive="{
                        device: device,
                        images: {
                            mobile: item.mobile,
                            tablet: item.tablet,
                            desktop: item.desktop
                        }
                    }" class="inner bg-image-to-load"></div>
                </div>
            </div>
        </div>

        <div class="slider-nav-wrapper" v-show="moreOneItem">
            <div class="nav-inner">
                <span class="dot" v-for="(item, index) in items" :key="index" :class="{ 'completed': nextIndex >= index }"
                    @click="setCurrent(index)"></span>

                <div class="progress-wrapper">
                    <div class="progress-inner">
                        <div class="bar progress-bar"></div>
                        <div class="bar"></div>
                    </div>
                </div>
            </div>

            <div class="btn-nav">
                <div class="btn-arrow" @click="previous"><span class="arrow arrow-gray"></span></div>
                <div class="btn-arrow btn-arrow-right" @click="next"><span class="arrow arrow-right arrow-gray"></span>
                </div>
            </div>
        </div>

        <div class="btn-close" @click="closeSlider"><span class="icon-cross"></span></div>
    </div>
</template>

<script setup>
import { useStore } from 'vuex';
import { usePointerSwipe } from '@vueuse/core';
import { gsap } from 'gsap';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import vImgResponsive from '@/directives/v-img-responsive';

const store = useStore();

const props = defineProps({
    autoplay: {
        type: Boolean,
        default: true
    },
    items: {
        type: Array,
        default: () => []
    },
    modal: {
        type: Boolean,
        default: false
    },
    opened: {
        type: Boolean,
        default: false
    },
    modalCurrentIndex: {
        type: [Number, Boolean],
        default: false
    }
});

const emit = defineEmits(['closeSlider']);

const slider = ref(null);
const currentIndex = ref(0);
const nextIndex = ref(0);
const autoplayDuration = ref(5);
const isAnimating = ref(false);
const timerAutoplay = ref(null);
const tlSlideTransition = ref(null);
const animProgressBar = ref(null);

const device = computed(() => store.state.device);
const pageLoaded = computed(() => store.state.pageLoaded);
const beforeIndex = computed(() => currentIndex.value === 0 ? props.items.length - 1 : currentIndex.value - 1);
const afterIndex = computed(() => currentIndex.value === props.items.length - 1 ? 0 : currentIndex.value + 1);
const moreOneItem = computed(() => props.items.length > 1);

onMounted(() => {
    // Autoplay
    if (props.autoplay && moreOneItem.value && pageLoaded.value && !props.modal) {
        runProgress();
    }
});

onBeforeUnmount(() => {
    clearAllAnimations();
});

const previous = () => {
    runTransition(beforeIndex.value, -1);
};

const next = () => {
    runTransition(afterIndex.value, 1);
};

const setCurrent = (index) => {
    if (index !== currentIndex.value) {
        let direction = index > currentIndex.value ? 1 : -1;
        runTransition(index, direction);
    }
};

const runProgress = () => {
    if (props.autoplay && moreOneItem.value) {
        let percent = 100 / props.items.length * (currentIndex.value + 1) + '%';
        if (animProgressBar.value) {
            animProgressBar.value.kill();
        }
        animProgressBar.value = gsap.to('.progress-wrapper .progress-bar', {
            duration: autoplayDuration.value,
            width: percent,
            height: percent,
            ease: 'linear.none',
            onComplete: () => {
                next();
            },
        });
    }
};

const runTransition = (newIndex, direction) => {
    if (!isAnimating.value) {
        nextIndex.value = newIndex;
        isAnimating.value = true;

        setProgress(nextIndex.value);

        clearTimeout(timerAutoplay.value);
        timerAutoplay.value = setTimeout(() => {
            const labelLeaveAnim = 'leave-anim-label';
            const labelEnterAnim = 'enter-anim-label';

            const itemEnter = document.querySelector('.item-enter');
            const backgroundEnter = itemEnter?.querySelector('.background') ?? null;
            const backgroundInnerEnter = backgroundEnter?.querySelector('.inner') ?? null;

            const itemLeave = document.querySelector('.item-leave');
            const backgroundLeave = itemLeave?.querySelector('.background') ?? null;
            const backgroundInnerLeave = backgroundLeave?.querySelector('.inner') ?? null;

            tlSlideTransition.value = gsap.timeline({
                onComplete: () => {
                    isAnimating.value = false;
                    currentIndex.value = nextIndex.value;
                    runProgress();
                },
            })
            .set(backgroundEnter, { x: '0%' })
            .set(backgroundInnerEnter, { x: (10 * direction) + '%' })
            .addLabel(labelLeaveAnim)
            .to(backgroundLeave, { duration: 1.3, x: -(100 * direction) + '%', ease: 'power4.inOut' }, labelLeaveAnim)
            .to(backgroundInnerLeave, { duration: 1.3, x: (90 * direction) + '%', ease: 'power4.inOut' }, labelLeaveAnim)
            .to(backgroundInnerEnter, { duration: 1.3, x: '0%', ease: 'power4.inOut' }, labelLeaveAnim)
            .addLabel(labelEnterAnim);
        }, 0);
    }
};

const setProgress = (toIndex) => {
    let percent = 100 / props.items.length * toIndex + '%';
    if (animProgressBar.value) {
        animProgressBar.value.kill();
    }
    animProgressBar.value = gsap.to('.progress-wrapper .progress-bar', {
        duration: 0.3,
        width: percent,
        height: percent,
        ease: 'power2.out',
    });
};

const clearAllAnimations = () => {
    clearTimeout(timerAutoplay.value);
    if (animProgressBar.value) {
        animProgressBar.value.kill();
    }
    if (tlSlideTransition.value) {
        tlSlideTransition.value.kill();
        gsap.set('.item .background', { clearProps: 'transform' });
        gsap.set('.item .background .inner', { clearProps: 'transform' });
    }

    isAnimating.value = false;
};

const closeSlider = () => {
    clearAllAnimations();
    emit('closeSlider');
};

const onDocumentKeyup = (e) => {
    switch (e.code) {
        case 'Escape':
            closeSlider();
            break;
        case 'ArrowLeft':
            previous();
            break;
        case 'Space':
            if (e.ctrlKey) {
                previous();
            } else {
                next();
            }
            break;
        case 'ArrowRight':
            next();
            break;
        default:
            return;
    }
};

usePointerSwipe(slider, {
    onSwipeEnd(e, direction) {
        if (moreOneItem.value) {
            switch (direction) {
                case 'left':
                    next();
                    break;
                case 'right':
                    previous();
                    break;
            }
        }
    },
});

watch(() => props.opened, (newState, oldState) => {
    if (!oldState && newState && props.modalCurrentIndex !== false) {
        currentIndex.value = props.modalCurrentIndex;
        runProgress();

        document.addEventListener('keyup', onDocumentKeyup);
    } else if (!newState) {
        document.removeEventListener('keyup', onDocumentKeyup);
    }
});

watch(() => pageLoaded.value, (newState, oldState) => {
    if (!oldState && newState && moreOneItem.value && timerAutoplay.value === null && pageLoaded.value && !props.modal) {
        runProgress();
    }
});
</script>
